import React, { useEffect, useMemo, useState } from 'react';
import { Link2Off } from 'lucide-react';
import { Canvas, FabricObject } from 'fabric';
import { AttachToConfig, AttachToSide } from '../utils/attach-to';
import { clearAttachTo, getAttachTo, listAnchorablePieceIds, setAttachTo } from '../utils/attach-to-accessors';
import { colors, typography } from '../../theme';

type Props = {
  obj: FabricObject;
  canvas: Canvas;
  onCommit?: (attachTo: AttachToConfig | undefined) => void;
  onSideChange?: (newSide: AttachToSide) => void;
};

export const AttachToRow: React.FC<Props> = ({ obj, canvas, onCommit, onSideChange }) => {
  const [cfg, setCfg] = useState<AttachToConfig | undefined>(getAttachTo(obj));
  const anchors = useMemo(() => listAnchorablePieceIds(canvas, obj), [canvas, obj]);

  useEffect(() => setCfg(getAttachTo(obj)), [obj]);

  const apply = (next: AttachToConfig) => {
    setCfg(next);
    setAttachTo(obj, next, canvas); // preview
    onCommit?.(next);
  };

  const remove = () => {
    setCfg(undefined);
    clearAttachTo(obj, canvas); // preview
    onCommit?.(undefined);
  };

  const onPickAnchor = (pieceId: string) => {
    if (!pieceId) return remove();
    apply({ pieceId, side: cfg?.side ?? 'right', gap: cfg?.gap ?? 0 });
  };

  return (
    <div style={sectionStyle}>
      <div style={labelStyle}>Anclaje</div>
      <div style={rowStyle}>
        <select value={cfg?.pieceId ?? ''} onChange={e => onPickAnchor(e.target.value)} style={selectStyle}>
          <option value=''>Ninguno</option>
          {anchors.map(id => (
            <option key={id} value={id}>
              {id}
            </option>
          ))}
        </select>
        {cfg && (
          <button onClick={remove} style={iconBtnStyle} title='Quitar anclaje'>
            <Link2Off size={14} />
          </button>
        )}
      </div>

      {cfg && (
        <>
          <div style={{ ...rowStyle, marginTop: 6 }}>
            <span style={subLabelStyle}>Lado</span>
            {(['left', 'right'] as AttachToSide[]).map(side => (
              <button
                key={side}
                onClick={() => {
                  if (cfg.side !== side) onSideChange?.(side);
                  apply({ ...cfg, side });
                }}
                style={cfg.side === side ? activeToggleStyle : toggleStyle}
              >
                {side === 'left' ? 'Izq' : 'Der'}
              </button>
            ))}
          </div>
          <div style={{ ...rowStyle, marginTop: 6 }}>
            <span style={subLabelStyle}>Gap</span>
            <input
              type='number'
              value={cfg.gap ?? 0}
              onChange={e => apply({ ...cfg, gap: Number(e.target.value) })}
              style={numberInputStyle}
            />
            <span style={suffixStyle}>px</span>
          </div>
        </>
      )}
    </div>
  );
};

const sectionStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', marginBottom: 14 };
const labelStyle: React.CSSProperties = {
  fontSize: typography.fontSize.base,
  fontFamily: typography.fontFamily.ui,
  color: colors.textMuted,
  marginBottom: 4
};
const subLabelStyle: React.CSSProperties = { ...labelStyle, minWidth: 70, marginBottom: 0 };
const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8 };
const baseInputStyle: React.CSSProperties = {
  padding: '6px 8px',
  border: `1px solid ${colors.border}`,
  borderRadius: 4,
  fontSize: typography.fontSize.base,
  fontFamily: typography.fontFamily.ui,
  background: colors.surfaceElevated,
  color: colors.textPrimary,
  outline: 'none'
};
const selectStyle: React.CSSProperties = { ...baseInputStyle, flex: 1 };
const numberInputStyle: React.CSSProperties = { ...baseInputStyle, width: 70 };
const suffixStyle: React.CSSProperties = {
  fontSize: typography.fontSize.base,
  fontFamily: typography.fontFamily.ui,
  color: colors.textSecondary
};
const toggleStyle: React.CSSProperties = {
  ...baseInputStyle,
  padding: '4px 10px',
  cursor: 'pointer'
};
const activeToggleStyle: React.CSSProperties = {
  ...toggleStyle,
  background: colors.textPrimary,
  color: colors.surfaceElevated,
  border: `1px solid ${colors.textPrimary}`
};
const iconBtnStyle: React.CSSProperties = {
  ...baseInputStyle,
  padding: 6,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center'
};
